import { useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { ChevronDown, ChevronRight, type LucideIcon } from './icons';
import { Icon } from './Icon';
import type { WsShellSection } from './shellTypes';
import { WS_COLOR, WS_CONTROL, WS_STATUS } from './tokens';

/** The one navigation row anatomy, shared by the rail and the drawer. */
export const NAV_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  width: '100%',
  minHeight: WS_CONTROL.hitArea,
  px: 3,
  border: 0,
  borderRadius: '22px',
  font: 'inherit',
  fontSize: 14,
  textAlign: 'left',
  cursor: 'pointer',
  color: WS_COLOR.text,
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: WS_COLOR.tonal },
} as const;

export interface NavRowProps {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  current?: boolean;
  depth?: number;
  trailing?: ReactNode;
  expanded?: boolean;
}

/** A navigation button. The current one uses the info pair and `aria-current`. */
export function NavRow({
  label,
  onClick,
  icon,
  current,
  depth = 0,
  trailing,
  expanded,
}: NavRowProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
      aria-expanded={expanded}
      sx={{
        ...NAV_ROW_SX,
        pl: 3 + depth * 4,
        fontWeight: current ? 600 : 400,
        color: current ? WS_STATUS.info.ink : WS_COLOR.text,
        backgroundColor: current ? WS_STATUS.info.fill : 'transparent',
        '&:hover': { backgroundColor: current ? WS_STATUS.info.fill : WS_COLOR.tonal },
      }}
    >
      {icon ? <Icon icon={icon} /> : null}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Box>
      {trailing}
    </Box>
  );
}

export interface NavSectionsProps {
  sections: readonly WsShellSection[];
  depth: number;
  onGo: (path: string) => void;
  activeSectionKey?: string;
  /** Keys of every section on the path to the current screen. */
  activeTrailKeys?: readonly string[];
}

/** A module's sections; a section with its own `sections` is a collapsible bucket. */
export function NavSections({
  sections,
  depth,
  onGo,
  activeSectionKey,
  activeTrailKeys = [],
}: NavSectionsProps) {
  // Only buckets the person has clicked; the rest follow the URL.
  const [openBuckets, setOpenBuckets] = useState<Record<string, boolean>>({});
  const render = (list: readonly WsShellSection[], level: number): ReactNode =>
    list.map((s) => {
      if (s.sections?.length) {
        const open = openBuckets[s.key] ?? activeTrailKeys.includes(s.key);
        return (
          <Box key={s.key}>
            <Box
              component="button"
              type="button"
              aria-expanded={open}
              onClick={() => setOpenBuckets((o) => ({ ...o, [s.key]: !open }))}
              sx={{
                ...NAV_ROW_SX,
                pl: 3 + level * 4,
                fontWeight: 600,
                color: activeTrailKeys.includes(s.key) ? WS_STATUS.info.ink : WS_COLOR.text,
              }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {s.label}
              </Box>
              <Icon icon={open ? ChevronDown : ChevronRight} size={16} />
            </Box>
            {open ? <Box>{render(s.sections, level + 1)}</Box> : null}
          </Box>
        );
      }
      return (
        <NavRow
          key={s.key}
          label={s.label}
          depth={level}
          current={s.key === activeSectionKey}
          onClick={() => onGo(s.path)}
        />
      );
    });
  return <>{render(sections, depth)}</>;
}
